import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { TASHKENT, loadYandexMaps, type YMap, type YMaps } from '@dorixona/ui'
import { useMapKeySource } from '../../api/mapsKey'

export interface MapPoint {
  lat: number
  lng: number
}

export interface AddressMapProps {
  /** Куда везти; null — точки нет, карта смотрит на Ташкент. */
  point: MapPoint | null
  onPick: (point: MapPoint) => void
}

/** Булавка точки доставки — та же, что у выбранной аптеки, без номера. */
const PIN = '<div class="lpin-at on"><div class="lpin on"><span></span></div></div>'

/** Координаты — до шести знаков: это около 10 см, точнее не нужно. */
const round = (n: number) => Math.round(n * 1e6) / 1e6

type State = 'loading' | 'ready' | 'failed'

/**
 * Карта выбора адреса (Яндекс Карты, общий загрузчик в `@dorixona/ui`):
 * нажатие ставит точку, выбор из поиска переставляет булавку и подводит к ней
 * карту. Не загрузилась — адрес по-прежнему находится поиском.
 */
export function AddressMap({ point, onPick }: AddressMapProps) {
  const { t } = useTranslation(['checkout', 'common'])
  const box = useRef<HTMLDivElement>(null)
  const api = useRef<YMaps | null>(null)
  const map = useRef<YMap | null>(null)
  const [state, setState] = useState<State>('loading')
  const keySource = useMapKeySource()
  // Карта создаётся один раз, а обработчик и точка меняются на каждом выборе.
  const pick = useRef(onPick)
  const latest = useRef(point)
  useEffect(() => {
    pick.current = onPick
    latest.current = point
  })

  useEffect(() => {
    let alive = true
    loadYandexMaps(keySource).then(
      (ymaps) => {
        if (!alive) return
        const start = latest.current
        const view = new ymaps.Map(
          box.current as HTMLDivElement,
          {
            center: start ? [start.lat, start.lng] : TASHKENT,
            zoom: start ? 16 : 12,
            controls: ['zoomControl'],
          },
          { suppressMapOpenBlock: true },
        )
        view.events.add('click', (event) => {
          const [lat, lng] = event.get('coords')
          pick.current({ lat: round(lat), lng: round(lng) })
        })
        api.current = ymaps
        map.current = view
        setState('ready')
      },
      () => {
        if (alive) setState('failed')
      },
    )
    return () => {
      alive = false
      map.current?.destroy()
      map.current = null
    }
  }, [keySource])

  const lat = point?.lat
  const lng = point?.lng
  useEffect(() => {
    if (state !== 'ready') return
    const ymaps = api.current as YMaps
    const view = map.current as YMap
    view.geoObjects.removeAll()
    if (lat === undefined || lng === undefined) return
    view.geoObjects.add(
      new ymaps.Placemark(
        [lat, lng],
        {},
        {
          iconLayout: ymaps.templateLayoutFactory.createClass(PIN),
          iconShape: { type: 'Circle', coordinates: [0, -25], radius: 18 },
        },
      ),
    )
    view.setCenter([lat, lng], Math.max(view.getZoom(), 16), { duration: 200 })
  }, [state, lat, lng])

  return (
    <div className="st-map addr-map-wrap">
      <div
        ref={box}
        className="addr-map"
        role="group"
        aria-label={t('checkout:addressPage.map')}
        aria-busy={state === 'loading'}
        data-state={state}
      >
        {state === 'failed' ? <span className="map-failed">{t('common:map.failed')}</span> : null}
      </div>
    </div>
  )
}
